import React, { useState, useEffect, useRef } from 'react'
import { Layout, Menu, Avatar, Dropdown, Button, Typography } from 'antd'
import type { MenuProps } from 'antd'
import {
  DashboardOutlined,
  UserOutlined,
  TeamOutlined,
  HomeOutlined,
  TruckOutlined,
  BarChartOutlined,
  SettingOutlined,
  LogoutOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  BellOutlined,
  UserAddOutlined,
  MessageOutlined,
} from '@ant-design/icons'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '@store'
import { logout } from '@store/slices/authSlice'

const { Header, Sider, Content } = Layout
const { Text } = Typography

interface DashboardLayoutProps {
  children: React.ReactNode
}

const DashboardLayout: React.FC<DashboardLayoutProps> = ({ children }) => {
  const [collapsed, setCollapsed] = useState(false)
  const navigate = useNavigate()
  const location = useLocation()
  const dispatch = useAppDispatch()
  const { admin } = useAppSelector((state) => state.auth)

  const INACTIVITY_TIMEOUT = 2 * 60 * 60 * 1000 // 2 小时不活动自动退出
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const logoutRef = useRef<() => void>(() => {})

  logoutRef.current = async () => {
    try {
      await dispatch(logout()).unwrap()
      navigate('/login')
    } catch (error) {
      console.error('登出失败:', error)
    }
  }

  // 事件处理器引用稳定，确保 removeEventListener 能正确移除
  const activityHandlerRef = useRef<() => void>(() => {})
  activityHandlerRef.current = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }
    timeoutRef.current = setTimeout(() => {
      logoutRef.current()
    }, INACTIVITY_TIMEOUT)
  }

  useEffect(() => {
    const handler = () => activityHandlerRef.current()
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart']
    events.forEach((event) => window.addEventListener(event, handler, { passive: true }))
    handler()

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
      events.forEach((event) => window.removeEventListener(event, handler))
    }
  }, [])

  const menuItems: MenuProps['items'] = [
    { key: '/dashboard', icon: <DashboardOutlined />, label: '仪表板' },
    { key: '/subscriptions', icon: <UserOutlined />, label: '订阅管理' },
    { key: '/users', icon: <TeamOutlined />, label: '用户管理' },
    { key: '/gardens', icon: <HomeOutlined />, label: '菜园管理' },
    { key: '/delivery', icon: <TruckOutlined />, label: '配送管理' },
    { key: '/statistics', icon: <BarChartOutlined />, label: '数据统计' },
    {
      key: '/admin-accounts',
      icon: <UserAddOutlined />,
      label: '子账号授权',
    },
    { key: '/feedback', icon: <MessageOutlined />, label: '留言反馈' },
    { key: '/settings', icon: <SettingOutlined />, label: '系统设置' },
  ]

  const userMenuItems: MenuProps['items'] = [
    { key: 'profile', label: '个人资料', onClick: () => navigate('/profile') },
    {
      key: 'admin-accounts',
      label: '子账号授权',
      onClick: () => navigate('/admin-accounts'),
    },
    { key: 'settings', label: '账户设置', onClick: () => navigate('/settings') },
    { type: 'divider' },
    {
      key: 'logout',
      label: '退出登录',
      icon: <LogoutOutlined />,
      onClick: () => logoutRef.current(),
    },
  ]

  const roleLabel =
    admin?.role === 'SUPER_ADMIN'
      ? '超级管理员'
      : admin?.role === 'ADMIN'
        ? '管理员'
        : '操作员'

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Sider
        trigger={null}
        collapsible
        collapsed={collapsed}
        style={{
          background: '#fff',
          boxShadow: '2px 0 8px 0 rgba(29, 35, 41, 0.05)',
        }}
      >
        <div style={{
          height: '64px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderBottom: '1px solid #f0f0f0',
        }}>
          {!collapsed ? (
            <Text strong style={{ fontSize: '18px', color: '#1890ff' }}>
              西舟菜园管理端
            </Text>
          ) : (
            <Text strong style={{ fontSize: '20px', color: '#1890ff' }}>
              管理
            </Text>
          )}
        </div>
        
        <Menu
          mode="inline"
          selectedKeys={[location.pathname]}
          items={menuItems}
          onClick={({ key }) => navigate(key)}
          style={{ border: 'none', marginTop: '8px' }}
        />
      </Sider>

      <Layout>
        <Header style={{
          background: '#fff',
          padding: '0 24px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          boxShadow: '0 1px 4px rgba(0, 21, 41, 0.08)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <Button
              type="text"
              icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
              onClick={() => setCollapsed(!collapsed)}
              style={{ fontSize: '16px', width: 64, height: 64 }}
            />
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <Button
              type="text"
              icon={<BellOutlined />}
              style={{ fontSize: '16px' }}
            />
            
            <Dropdown menu={{ items: userMenuItems }} placement="bottomRight" arrow>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  cursor: 'pointer',
                  padding: '6px 10px',
                  borderRadius: 8,
                  transition: 'background-color 0.2s',
                  maxWidth: 220,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#f5f5f5'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent'
                }}
              >
                <Avatar
                  size="small"
                  icon={<UserOutlined />}
                  style={{ backgroundColor: '#1890ff', flexShrink: 0 }}
                />
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    justifyContent: 'center',
                    minWidth: 0,
                    lineHeight: 1.25,
                  }}
                >
                  <Text
                    strong
                    style={{
                      fontSize: 14,
                      display: 'block',
                      maxWidth: '100%',
                    }}
                    ellipsis={{ tooltip: admin?.username }}
                  >
                    {admin?.username ?? '—'}
                  </Text>
                  <Text type="secondary" style={{ fontSize: 12, marginTop: 2 }}>
                    {roleLabel}
                  </Text>
                </div>
              </div>
            </Dropdown>
          </div>
        </Header>

        <Content style={{
          margin: '24px',
          padding: '24px',
          background: '#fff',
          borderRadius: '8px',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
          minHeight: 'calc(100vh - 112px)',
        }}>
          {children}
        </Content>
      </Layout>
    </Layout>
  )
}

export default DashboardLayout



